import OutsidePressHandlerRoot from "react-native-outside-press";
import { ComponentProps, Ref, useEffect, useRef } from "react";
import { View } from "react-native";
import mergeRefs from "merge-refs";

type OutsideEventHandler = (event?: Event) => void;

const useOutsideEvents = <T extends HTMLElement>(
    ref: React.RefObject<T | null>,
    onOutside: OutsideEventHandler,
    disabled?: boolean,
) => {
    useEffect(() => {
        if (!disabled) {
            const handleEvent = (e: Event) => {
                if (ref.current && !ref.current.contains(e.target as Node)) {
                    onOutside(e);
                }
            };

            document.addEventListener("scroll", handleEvent, { capture: true, once: true });
            // We use "mousedown" event instead of "click" event because we use long-press clicks to display some components,
            // and "click" would wrongly call the onOutside event.
            document.addEventListener("mousedown", handleEvent);
            document.addEventListener("touchstart", handleEvent);

            return () => {
                document.removeEventListener("scroll", handleEvent, { capture: true });
                document.removeEventListener("mousedown", handleEvent);
                document.removeEventListener("touchstart", handleEvent);
            };
        }
    }, [disabled, onOutside, ref]);
};

type OutsidePressHandlerProps = ComponentProps<typeof OutsidePressHandlerRoot>;

export const OutsidePressHandler = ({
    onOutsidePress,
    children,
    disabled,
    ref,
    ...props
}: OutsidePressHandlerProps & { ref?: Ref<HTMLDivElement> }) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useOutsideEvents(menuRef, onOutsidePress, disabled);

    return (
        <View ref={mergeRefs(ref, menuRef) as Ref<View>} {...props}>
            {children}
        </View>
    );
};
